import { useCallback, useEffect, useRef } from 'react';

/**
 * A refresh signal that is HELD while the UI is busy and applied when it frees.
 *
 * The old panel gates its socket-driven refetch on `refetchOrders &&
 * !isDrawerOpen` (`merchant-panel/src/pages/delivery/index.tsx`): a `refresh`
 * push that lands while the order drawer is open is deliberately not acted on.
 * Refetching underneath an open drawer re-sorts and re-pages the list the
 * merchant is reading, and can swap the row whose detail is on screen.
 *
 * The suppression is not a DROP, though — that would leave the list stale for
 * as long as the drawer stays open. Pushes that arrive while suppressed set a
 * pending flag, and the refetch fires once on release. Several suppressed
 * pushes collapse into one refetch, which is what you want: `refresh` carries
 * no payload, so two of them mean the same thing as one.
 *
 * The held flag is a REF, not state. It is never rendered, and holding it in
 * state would mean either a synchronous setState inside an effect (a cascading
 * render, which `react-hooks/set-state-in-effect` rejects) or firing the
 * callback from inside a state updater, which must stay pure — React may
 * invoke an updater twice, which would refetch twice.
 */
export function useDeferredRefresh(
  refresh: () => void,
  { suppressed }: { suppressed: boolean },
): () => void {
  /** Whether a push arrived while suppressed and still owes a refetch. */
  const pendingRef = useRef(false);

  // Read through refs so the release effect depends only on `suppressed` —
  // an inline `refresh` callback would otherwise re-run it every render.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  const suppressedRef = useRef(suppressed);
  useEffect(() => {
    suppressedRef.current = suppressed;
  }, [suppressed]);

  const signal = useCallback(() => {
    if (suppressedRef.current) {
      pendingRef.current = true;
      return;
    }
    refreshRef.current();
  }, []);

  // Release: the moment suppression lifts, apply a held signal (once).
  useEffect(() => {
    if (suppressed || !pendingRef.current) return;
    pendingRef.current = false;
    refreshRef.current();
  }, [suppressed]);

  return signal;
}
